import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, History, Settings, ChevronDown, Shield } from 'lucide-react';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  return (
    <header className="bg-white border-b border-bastion-gray-200 sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/bastion-logo.png"
              alt="Bastion Handwriting"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/tools" className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide transition-colors">
              Worksheet Tools
            </Link>
            <Link to="/analysis" className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide transition-colors">
              AI Analysis
            </Link>
            <Link to="/pricing" className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide transition-colors">
              Pricing
            </Link>
            <a
              href="https://bastionboltactionpen.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide transition-colors"
            >
              Shop Pens
            </a>
          </div>

          {/* Auth Buttons / User Menu */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-bastion-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-bastion-black rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-medium text-bastion-gray-800 text-sm">
                    {user?.name || user?.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-bastion-gray-500" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-bastion-gray-200 py-2">
                    <div className="px-4 py-2 border-b border-bastion-gray-100">
                      <p className="text-sm text-bastion-gray-500">Credits</p>
                      <p className="font-bold text-bastion-black">{user?.credits || 0}</p>
                    </div>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-bastion-red hover:bg-red-50 text-sm font-medium"
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-bastion-gray-700 hover:bg-bastion-gray-100 text-sm"
                    >
                      <History className="w-4 h-4" />
                      Analysis History
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-bastion-gray-700 hover:bg-bastion-gray-100 text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 text-bastion-gray-700 hover:bg-bastion-gray-100 w-full text-left text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide transition-colors">
                  Login
                </Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-5 uppercase tracking-bastion-wide">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-bastion-gray-100"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-bastion-black" />
            ) : (
              <Menu className="w-6 h-6 text-bastion-black" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-bastion-gray-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/tools"
                onClick={() => setMobileMenuOpen(false)}
                className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide"
              >
                Worksheet Tools
              </Link>
              <Link
                to="/analysis"
                onClick={() => setMobileMenuOpen(false)}
                className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide"
              >
                AI Analysis
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide"
              >
                Pricing
              </Link>
              <a
                href="https://bastionboltactionpen.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide"
              >
                Shop Pens
              </a>

              <div className="border-t border-bastion-gray-200 pt-4 mt-2">
                {isAuthenticated ? (
                  <>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-bastion-red font-medium text-sm uppercase tracking-bastion-wide mb-4"
                      >
                        Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide mb-4"
                    >
                      Analysis History
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide mb-4"
                    >
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="text-bastion-black hover:text-bastion-red font-medium text-sm uppercase tracking-bastion-wide"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-secondary text-center text-sm uppercase tracking-bastion-wide"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-primary text-center text-sm uppercase tracking-bastion-wide"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
